import { Link } from "react-router-dom";
import { useContent } from "@/context/ContentContext";
import { Mail, Phone, Home as HomeIcon } from "lucide-react";

// Renderuje pojedynczy link — obsługuje 3 warianty:
// - href zaczyna się od "/" -> Link (SPA)
// - kind = "cookie-settings" -> button otwierający ponownie baner
// - inne href -> zewnętrzny <a>
function FooterLink({ item }) {
  if (item.kind === "cookie-settings") {
    return (
      <button
        onClick={() => {
          localStorage.removeItem("rm_cookie_consent");
          window.location.reload();
        }}
        className="hover:text-white text-left"
        data-testid={`footer-link-${item.id}`}
      >
        {item.label}
      </button>
    );
  }
  const href = item.href || "";
  if (href.startsWith("/")) {
    return (
      <Link to={href} className="hover:text-white" data-testid={`footer-link-${item.id}`}>
        {item.label}
      </Link>
    );
  }
  return (
    <a
      href={href || "#"}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
      className="hover:text-white"
      data-testid={`footer-link-${item.id}`}
    >
      {item.label}
    </a>
  );
}

export default function Footer() {
  const { brand } = useContent();
  const columns = brand.footer_columns || [];
  // Grid dynamiczny: 1 (logo) + N (kolumny) + 1 (kontakt) — max ~5, min 3
  const totalCols = 2 + columns.length; // logo + kontakt + edytowalne kolumny
  const gridCls =
    totalCols >= 5 ? "md:grid-cols-5" :
    totalCols === 4 ? "md:grid-cols-4" :
    totalCols === 3 ? "md:grid-cols-3" : "md:grid-cols-2";

  return (
    <footer
      data-testid="footer"
      className="mt-24 border-t border-white/10 bg-[#15296b] text-white/80"
    >
      <div className={`max-w-7xl mx-auto px-6 py-14 grid gap-10 ${gridCls}`}>
        <div>
          <div className="flex items-center gap-2 mb-4">
            <img
              src={brand?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${brand.logo_url}` : "/logo.jpg"}
              alt="Rmobile"
              onError={(e) => { if (!e.currentTarget.dataset.fallback) { e.currentTarget.dataset.fallback = "1"; e.currentTarget.src = "/logo.jpg"; } }}
              onLoad={(e) => { if (e.currentTarget.naturalWidth < 20 && !e.currentTarget.dataset.fallback) { e.currentTarget.dataset.fallback = "1"; e.currentTarget.src = "/logo.jpg"; } }}
              className="w-[75px] h-[75px] object-cover"
            />
          </div>
          <p className="text-sm text-white/60 max-w-xs whitespace-pre-line">
            {brand.tagline}
          </p>
        </div>

        {columns.map((col) => (
          <div key={col.id} data-testid={`footer-column-${col.id}`}>
            <h4 className="font-display font-bold text-white mb-4">{col.title}</h4>
            <ul className="space-y-2 text-sm">
              {(col.links || []).map((link) => (
                <li key={link.id}>
                  <FooterLink item={link} />
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div>
          <h4 className="font-display font-bold text-white mb-4">Kontakt</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2"><Phone size={16} className="mt-0.5 flex-shrink-0" /> {brand.contact.phone}</li>
            <li className="flex items-start gap-2"><Mail size={16} className="mt-0.5 flex-shrink-0" /> {brand.contact.email}</li>
            {brand.contact.address && (
              <li className="flex items-start gap-2">
                <HomeIcon size={16} className="mt-0.5 flex-shrink-0" />
                <span className="whitespace-pre-line">{brand.contact.address}</span>
              </li>
            )}
            {brand.contact.hours && (
              <li className="text-xs text-white/60 mt-2 pt-2 border-t border-white/10">
                <span className="text-white/50 uppercase tracking-wider">Infolinia:</span>
                <div className="whitespace-pre-line mt-1">{brand.contact.hours}</div>
              </li>
            )}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-white/50">
        © {new Date().getFullYear()} {brand.name}. Wszelkie prawa zastrzeżone.
      </div>
    </footer>
  );
}
